import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { assertNativeApiUrl, fetchNativeAttachmentBlob, isApiUrl } from "./runtime";

const EXPORT_CACHE_ROOT = "jfm-exports";
export const EXPORT_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const EXPORT_FOLDER = /^(\d+)-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

function downloadUrl(value) {
  const base = Capacitor.isNativePlatform()
    ? `${assertNativeApiUrl(process.env.REACT_APP_BACKEND_URL)}/`
    : window.location.href;
  return new URL(value, base);
}

function safeFilename(value) {
  const filename = String(value || "download").normalize("NFC")
    .replace(/\\/g, "/").split("/").filter((part) => part && part !== "." && part !== "..").join("_")
    .replace(/[\u0000-\u001f\u007f<>:"|?*\u202a-\u202e\u2066-\u2069]/g, "_")
    .replace(/^\.+/, "").replace(/[. ]+$/, "");
  if (!filename) return "download";
  if (filename.length <= 180) return filename;
  const extension = filename.match(/\.[a-z0-9]{1,12}$/i)?.[0] || "";
  return filename.slice(0, 180 - extension.length) + extension;
}

function randomId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function base64Bytes(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",", 2)[1]);
    reader.onerror = () => reject(reader.error || new Error("Could not read the downloaded file."));
    reader.onabort = () => reject(new Error("Reading the downloaded file was interrupted."));
    reader.readAsDataURL(blob);
  });
}

export async function cleanupExportCache() {
  if (!Capacitor.isNativePlatform()) return;
  // Native recipients may still read a shared URI after their sheet closes.
  // Never delete the just-shared file. Reap only our old export directories
  // during a later save or app startup, leaving all other cache files alone.
  try {
    const { files } = await Filesystem.readdir({ path: EXPORT_CACHE_ROOT, directory: Directory.Cache });
    const cutoff = Date.now() - EXPORT_CACHE_MAX_AGE_MS;
    await Promise.allSettled(files.filter((entry) => {
      const match = EXPORT_FOLDER.exec(entry.name);
      return entry.type === "directory" && match && Number(match[1]) < cutoff;
    }).map((entry) => Filesystem.rmdir({
      path: `${EXPORT_CACHE_ROOT}/${entry.name}`, directory: Directory.Cache, recursive: true,
    })));
  } catch {
    // First save has no directory yet; cleanup must not prevent a new export.
  }
}

/** Deliver existing bytes using the platform's download or save/share UI.
 * A native "shared" outcome means the OS accepted the share, not that the
 * recipient saved a file. "cancelled" is a normal outcome, not a download error.
 */
export async function saveBlob(blob, filename) {
  if (!(blob instanceof Blob)) throw new TypeError("The downloaded file must be a Blob.");
  const name = safeFilename(filename);
  if (!Capacitor.isNativePlatform()) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = name;
    document.body.appendChild(anchor);
    try {
      anchor.click();
    } finally {
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
    return { status: "saved" };
  }

  await cleanupExportCache();
  const data = await base64Bytes(blob);
  const path = `${EXPORT_CACHE_ROOT}/${Date.now()}-${randomId()}/${name}`;
  const { uri } = await Filesystem.writeFile({ path, directory: Directory.Cache, data, recursive: true });
  try {
    await Share.share({ files: [uri], title: name, dialogTitle: "Save or share file" });
    return { status: "shared" };
  } catch (error) {
    // Both native implementations reject this message when their sheet closes.
    // An empty activityType on success can be legitimate and is not cancellation.
    if (error?.name === "AbortError" || /^Share cancel(?:ed|led)\.?$/i.test(error?.message || "")) {
      return { status: "cancelled" };
    }
    throw error;
  }
}

export async function saveAuthenticatedDownload(url, filename, config = {}) {
  const { openInNewTab = false, ...requestConfig } = config;
  const target = downloadUrl(url);
  if (!isApiUrl(target)) throw new Error("Downloads must use this application's API.");
  if (!Capacitor.isNativePlatform()) {
    // Preserve normal browser navigation to the protected endpoint. Its signed
    // storage redirect can download without introducing cross-origin fetch.
    const anchor = document.createElement("a");
    anchor.href = target.href;
    anchor.download = safeFilename(filename);
    if (openInNewTab) { anchor.target = "_blank"; anchor.rel = "noopener noreferrer"; }
    document.body.appendChild(anchor);
    try { anchor.click(); } finally { anchor.remove(); }
    return { status: "saved" };
  }
  const response = await axios.get(target.href, { ...requestConfig, responseType: "blob", withCredentials: true });
  return saveBlob(response.data, filename);
}

// Attachment URLs supplied by the API are either protected API endpoints or
// short-lived storage URLs. External downloads must bypass the API client.
export async function saveAttachmentDownload(url, filename) {
  const target = downloadUrl(url);
  const native = Capacitor.isNativePlatform();
  if (target.username || target.password || (native && target.protocol !== "https:")) {
    throw new Error("Attachment downloads require an HTTPS URL without credentials.");
  }
  if (isApiUrl(target)) return saveAuthenticatedDownload(target.href, filename);
  if (target.protocol !== "https:") {
    throw new Error("Attachment downloads require an HTTPS URL without credentials.");
  }
  if (native) return saveBlob(await fetchNativeAttachmentBlob(target.href), filename);

  const anchor = document.createElement("a");
  anchor.href = target.href;
  anchor.download = safeFilename(filename);
  document.body.appendChild(anchor);
  try { anchor.click(); } finally { anchor.remove(); }
  return { status: "saved" };
}

